/**
 * Utility function to extract date of birth from MYKAD number
 * MYKAD format: YYMMDD-PB-XXXX
 *
 * @param {string} myKadNumber - The MYKAD number in format YYMMDD-PB-XXXX
 * @returns {string|null} - Extracted date of birth as YYYY-MM-DD string or null if invalid
 */
export const extractDOBFromMyKad = (myKadNumber) => {
    if (!myKadNumber || typeof myKadNumber !== 'string') {
        return null;
    }
    const cleanNumber = myKadNumber.replace(/\s/g, '').toUpperCase();
    const myKadRegex = /^(\d{2})(\d{2})(\d{2})-\d{2}-\d{4}$/;
    const match = cleanNumber.match(myKadRegex);
    if (!match) {
        return null;
    }
    const [, year, month, day] = match;
    let fullYear = parseInt(year);
    if (fullYear <= 29) {
        fullYear += 2000;
    } else {
        fullYear += 1900;
    }

    try {
        // Validate month and day
        const monthNum = parseInt(month);
        const dayNum = parseInt(day);
        
        if (monthNum < 1 || monthNum > 12) {
            return null;
        }
        
        if (dayNum < 1 || dayNum > 31) {
            return null;
        }
        
        // Check if the year is reasonable
        const currentYear = new Date().getFullYear();
        if (fullYear > currentYear || fullYear < 1900) {
            return null;
        }
        
        // Validate the date by creating a Date object and checking if values match
        // Use UTC to avoid timezone issues
        const testDate = new Date(Date.UTC(fullYear, monthNum - 1, dayNum));
        if (testDate.getUTCFullYear() !== fullYear || 
            testDate.getUTCMonth() !== monthNum - 1 || 
            testDate.getUTCDate() !== dayNum) {
            // Invalid date (e.g., Feb 30)
            return null;
        }
        
        // Return as YYYY-MM-DD string directly - no timezone conversion needed
        const dateString = `${fullYear}-${month}-${day}`;
        return dateString;
    } catch (error) {
        console.error('Error parsing date from MyKad:', error);
        return null;
    }
};

/**
 * Example usage and test cases
 */
export const myKadExamples = () => {
    const examples = [
        '900315-01-1234', // 15 March 1990
        '050820-02-5678', // 20 August 2005
        '851230-03-9999', // 30 December 1985
        '000101-04-0001', // 1 January 2000
        '991231-05-8888', // 31 December 1999
    ];

    console.log('MYKAD Date of Birth Extraction Examples:');
    console.log('========================================');

    examples.forEach((myKad, index) => {
        const dob = extractDOBFromMyKad(myKad);
        if (dob) {
            console.log(`${index + 1}. MYKAD: ${myKad} → DOB: ${dob}`);
        } else {
            console.log(`${index + 1}. MYKAD: ${myKad} → Invalid format or date`);
        }
    });

    // Test invalid formats
    console.log('\nInvalid Format Tests:');
    console.log('=====================');
    const invalidExamples = [
        '123456-78-9012', // Wrong format
        '900315-01-123',  // Missing digit
        '900315-01-12345', // Extra digit
        '900315011234',   // Missing hyphens
        '900315-01-1234-extra', // Extra characters
    ];

    invalidExamples.forEach((invalid, index) => {
        const dob = extractDOBFromMyKad(invalid);
        console.log(`${index + 1}. ${invalid} → ${dob ? 'Unexpectedly valid' : 'Correctly invalid'}`);
    });
};

// Export for testing
const myKadDOBExtractor = {
    extractDOBFromMyKad,
    myKadExamples
};

export default myKadDOBExtractor;
